import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { listBrands, getBrand } from '../utils/phoneCatalog'
import { comparePhoneModelsByNewness } from '../utils/phoneModelSort'
import ManufacturerLogo from './ManufacturerLogo'
import { keepClickOnTouch } from '../utils/keepClickOnTouch'

interface Listing {
  id: number
  title: string
  phone_model: { manufacturer: string; model_name: string }
}

interface CatalogBrowserProps {
  /** Listings shown in the catalog — used to count how many are for sale per option. */
  listings: Listing[]
  /** When user picks an item, called with a query string that filters catalog. */
  onPick: (query: string) => void
  /** Clear any current selection. */
  onClear: () => void
  /** Current catalog search — syncs storage highlight. */
  activeQuery?: string
  /** Externally pre-select a brand (e.g. when navigated from home tile). */
  presetBrand?: string | null
  /** Bumped when preset changes, even if the brand string is the same. */
  presetTick?: number
  className?: string
}

/**
 * Curated three-level catalog browser: Brand → Model → Storage.
 * Uses a static, hand-picked catalog (only real smartphones, no Wikipedia noise).
 * Shows badge with active listing count per option so the user sees what's available now.
 */
export default function CatalogBrowser({
  listings,
  onPick,
  onClear,
  activeQuery = '',
  presetBrand,
  presetTick,
  className = '',
}: CatalogBrowserProps) {
  const { t } = useTranslation()
  const [brand, setBrand] = useState<string | null>(null)
  const [model, setModel] = useState<string | null>(null)
  const [showAllBrands, setShowAllBrands] = useState(false)
  const [showAllModels, setShowAllModels] = useState(true)
  const [modelQuery, setModelQuery] = useState('')

  /** Apply external preset (e.g. when user clicks a brand tile on home). */
  useEffect(() => {
    if (!presetBrand) return
    // Find matching canonical brand name (case-insensitive)
    const match = listBrands().find((b) => b.toLowerCase() === presetBrand.toLowerCase())
    if (match) {
      setBrand(match)
      setModel(null)
      setModelQuery('')
      setShowAllModels(true) // open ALL models immediately, no "more" toggle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetBrand, presetTick])

  /** Count listings whose manufacturer matches the brand (case-insensitive contains). */
  const brandCounts = useMemo(() => {
    const m = new Map<string, number>()
    listings.forEach((l) => {
      const mfg = l.phone_model.manufacturer.toLowerCase()
      listBrands().forEach((b) => {
        if (mfg.includes(b.toLowerCase()) || b.toLowerCase().includes(mfg)) {
          m.set(b, (m.get(b) || 0) + 1)
        }
      })
    })
    return m
  }, [listings])

  /** For a brand+model, count listings whose model_name or title matches. */
  function countForModel(b: string, modelName: string): number {
    const target = modelName.toLowerCase()
    let n = 0
    listings.forEach((l) => {
      if (!l.phone_model.manufacturer.toLowerCase().includes(b.toLowerCase()) &&
          !b.toLowerCase().includes(l.phone_model.manufacturer.toLowerCase())) return
      const hay = `${l.phone_model.model_name} ${l.title}`.toLowerCase()
      if (hay.includes(target)) n += 1
    })
    return n
  }

  const sortedBrands = useMemo(() => {
    // Show brands in BRAND_ORDER but float ones with active listings to the top
    return listBrands().sort((a, b) => {
      const ca = brandCounts.get(a) || 0
      const cb = brandCounts.get(b) || 0
      if (cb !== ca) return cb - ca
      return 0
    })
  }, [brandCounts])

  const TOP = 5

  const currentBrand = brand ? getBrand(brand) : null
  // Models for selected brand — sort: ones with active listings first, then catalog order
  const sortedModels = useMemo(() => {
    if (!brand || !currentBrand) return [] as string[]
    return [...currentBrand.all].sort((a, b) => {
      const ca = countForModel(brand, a)
      const cb = countForModel(brand, b)
      if (cb !== ca) return cb - ca
      const idxA = currentBrand.all.indexOf(a)
      const idxB = currentBrand.all.indexOf(b)
      if (idxA !== idxB) return idxB - idxA
      return comparePhoneModelsByNewness(a, b)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, listings])

  const popularModels = sortedModels.slice(0, TOP)
  const moreModels = sortedModels.slice(TOP)

  // Storage options — from brand catalog; mark which actually have listings
  const storageCounts = useMemo(() => {
    if (!brand || !model || !currentBrand) return new Map<string, number>()
    const m = new Map<string, number>()
    listings.forEach((l) => {
      const mfgOk = l.phone_model.manufacturer.toLowerCase().includes(brand.toLowerCase()) ||
                    brand.toLowerCase().includes(l.phone_model.manufacturer.toLowerCase())
      if (!mfgOk) return
      const hay = `${l.phone_model.model_name} ${l.title}`.toLowerCase()
      if (!hay.includes(model.toLowerCase())) return
      currentBrand.storages.forEach((s) => {
        const re = new RegExp(s.replace('GB', '\\s?GB').replace('TB', '\\s?TB'), 'i')
        if (re.test(l.title)) m.set(s, (m.get(s) || 0) + 1)
      })
    })
    return m
  }, [listings, brand, model, currentBrand])

  function reset() {
    setBrand(null)
    setModel(null)
    setShowAllBrands(false)
    setShowAllModels(false)
    setModelQuery('')
    onClear()
  }

  function pickBrand(b: string) {
    setBrand(b)
    setModel(null)
    setShowAllModels(false)
    setModelQuery('')
    onPick(b)
  }

  function pickModel(m: string) {
    setModel(m)
    onPick(`${brand} ${m}`)
  }

  function pickStorage(s: string) {
    onPick(`${brand} ${model} ${s}`)
  }

  function clearStorageFilter() {
    if (brand && model) onPick(`${brand} ${model}`)
  }

  const selectedStorage = useMemo(() => {
    if (!brand || !model || !currentBrand) return null
    const q = activeQuery.toLowerCase()
    for (const s of currentBrand.storages) {
      if (q.includes(s.toLowerCase())) return s
    }
    return null
  }, [activeQuery, brand, model, currentBrand])

  const storagesAvailable = currentBrand
    ? currentBrand.storages.filter((s) => (storageCounts.get(s) || 0) > 0)
    : []

  return (
    <div className={`catalog-browser catalog-browser--app ${className}`.trim()}>
      <div className="catalog-browser__glow" aria-hidden="true" />

      {brand ? (
        <div className="catalog-browser__crumbs">
          <button type="button" onMouseDown={keepClickOnTouch} onClick={reset} className="catalog-browser__crumb">
            ‹ {t('catalog.browser.allManufacturers')}
          </button>
          <span className="catalog-browser__sep">›</span>
          <button
            type="button"
            onMouseDown={keepClickOnTouch}
            onClick={() => { setModel(null); onPick(brand) }}
            className={`catalog-browser__crumb${!model ? ' catalog-browser__crumb--active' : ''}`}
          >
            {brand}
          </button>
          {model ? (
            <>
              <span className="catalog-browser__sep">›</span>
              <button
                type="button"
                onMouseDown={keepClickOnTouch}
                onClick={() => onPick(`${brand} ${model}`)}
                className="catalog-browser__crumb catalog-browser__crumb--active"
              >
                {model}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* ── Step 1: Brand ─────────────────────────────────────────────────── */}
      {!brand && (() => {
        const brandsActive = sortedBrands.filter((b) => (brandCounts.get(b) || 0) > 0)
        const brandsExtra = sortedBrands.filter((b) => (brandCounts.get(b) || 0) === 0)
        return (
          <div key="step-brands" className="catalog-browser__step catalog-browser__step--brands">
            <div className="catalog-browser__brands-bar">
              <div className="catalog-browser__brands-head">
                <span className="catalog-filter-field__label">{t('catalog.browser.manufacturerLabel')}</span>
                {brandsExtra.length > 0 ? (
                  <button
                    type="button"
                    className={`catalog-filters-panel__more${showAllBrands ? ' catalog-filters-panel__more--active' : ''}`}
                    aria-expanded={showAllBrands}
                    onMouseDown={keepClickOnTouch}
                    onClick={() => setShowAllBrands((v) => !v)}
                  >
                    {showAllBrands
                      ? t('catalog.browser.collapse')
                      : t('catalog.browser.otherBrands', { count: brandsExtra.length })}
                  </button>
                ) : null}
              </div>
              <div className="catalog-browser__rail catalog-browser__rail--brands" role="list">
                {brandsActive.map((b, i) => (
                  <BrandChip key={b} brand={b} count={brandCounts.get(b) || 0} onPick={pickBrand} rail index={i} />
                ))}
              </div>
              {showAllBrands && brandsExtra.length > 0 ? (
                <div className="catalog-browser__brand-grid" role="list">
                  {brandsExtra.map((b, i) => (
                    <BrandChip key={b} brand={b} count={0} onPick={pickBrand} secondary index={i} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )
      })()}

      {/* ── Step 2: Model (vertical list — easier to scan) ────────────────── */}
      {brand && !model && currentBrand && (
        <div key={`step-models-${brand}`} className="catalog-browser__step catalog-browser__step--models">
          <div className="catalog-browser__step-head catalog-browser__step-head--brand">
            <span className="catalog-browser__brand-badge">
              <ManufacturerLogo manufacturer={brand} size={36} />
            </span>
            <div>
              <div className="catalog-browser__step-label">{t('catalog.browser.pickModel', { brand })}</div>
              <p className="catalog-browser__step-lead">
                {t('catalog.browser.modelsInCatalog', { count: sortedModels.length })}
              </p>
            </div>
          </div>
          <div className="catalog-browser__search-wrap">
            <input
              type="search"
              className="catalog-browser__model-search"
              placeholder={t('listing.builder.modelSearchPlaceholder', { brand })}
              value={modelQuery}
              onChange={(e) => setModelQuery(e.target.value)}
              aria-label={t('catalog.browser.modelSearchAria', { brand })}
            />
          </div>
          {(() => {
            const q = modelQuery.trim().toLowerCase()
            const base = q ? sortedModels : (showAllModels ? sortedModels : popularModels)
            const filtered = q ? base.filter((m) => m.toLowerCase().includes(q)) : base
            if (filtered.length === 0) {
              return (
                <p className="catalog-browser__empty">
                  {t('catalog.browser.modelsNotFound', { query: modelQuery })}
                </p>
              )
            }
            return (
              <div className="catalog-browser__list">
                {filtered.map((m, i) => (
                  <ModelRow key={m} name={m} count={countForModel(brand, m)} onPick={pickModel} index={i} />
                ))}
              </div>
            )
          })()}
          {!modelQuery.trim() && moreModels.length > 0 && (
            <button
              type="button"
              className="catalog-browser__more"
              onMouseDown={keepClickOnTouch}
              onClick={() => setShowAllModels((v) => !v)}
            >
              {showAllModels
                ? t('catalog.browser.showPopularOnly')
                : t('catalog.browser.allBrandModels', { brand, count: sortedModels.length })}
            </button>
          )}
        </div>
      )}

      {/* ── Step 3: Storage ───────────────────────────────────────────────── */}
      {brand && model && currentBrand && (
        <div key={`step-storage-${brand}-${model}`} className="catalog-browser__step catalog-browser__step--storage">
          <div className="catalog-browser__step-head catalog-browser__step-head--compact">
            <span className="catalog-filter-field__label">{t('catalog.browser.storageOptional')}</span>
          </div>
          {storagesAvailable.length === 0 ? (
            <p className="catalog-browser__storage-hint">
              {t('catalog.browser.storageNoListingsHint', { model })}
            </p>
          ) : null}
          <div className="catalog-browser__grid catalog-browser__grid--storages">
            <button
              type="button"
              className={`catalog-browser__chip catalog-browser__chip--storage${!selectedStorage ? ' catalog-browser__chip--selected' : ''}`}
              onMouseDown={keepClickOnTouch}
              onClick={clearStorageFilter}
            >
              {t('catalog.browser.storageAll')}
            </button>
            {currentBrand.storages.map((s, i) => {
              const c = storageCounts.get(s) || 0
              const disabled = c === 0
              const selected = selectedStorage === s
              return (
                <button
                  key={s}
                  type="button"
                  className={`catalog-browser__chip catalog-browser__chip--storage${selected ? ' catalog-browser__chip--selected' : ''}${disabled ? ' catalog-browser__chip--disabled' : ''}`}
                  style={{ '--chip-i': i } as CSSProperties}
                  onMouseDown={keepClickOnTouch}
                  onClick={() => pickStorage(s)}
                  title={disabled ? t('catalog.browser.storageNoListingsTitle') : undefined}
                >
                  <span>{s}</span>
                  {c > 0 ? (
                    <span className="catalog-browser__chip-count catalog-browser__chip-count--on-dark">{c}</span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

interface BrandChipProps {
  brand: string
  count: number
  onPick: (b: string) => void
  secondary?: boolean
  rail?: boolean
  index?: number
}
function BrandChip({ brand, count, onPick, secondary, rail, index = 0 }: BrandChipProps) {
  return (
    <button
      type="button"
      className={`catalog-browser__chip${secondary ? ' catalog-browser__chip--secondary' : ''}${rail ? ' catalog-browser__chip--rail' : ''}`}
      style={{ '--chip-i': index } as CSSProperties}
      onMouseDown={keepClickOnTouch}
      onClick={() => onPick(brand)}
    >
      <span className="catalog-browser__chip-name">{brand}</span>
      {count > 0 && <span className="catalog-browser__chip-count">{count}</span>}
      {!rail ? <span className="catalog-browser__chip-arrow">›</span> : null}
    </button>
  )
}

interface ModelRowProps { name: string; count: number; onPick: (m: string) => void; index?: number }
function ModelRow({ name, count, onPick, index = 0 }: ModelRowProps) {
  const empty = count === 0
  return (
    <button
      type="button"
      className={`catalog-browser__row${empty ? ' catalog-browser__row--empty' : ''}`}
      style={{ '--row-i': index } as CSSProperties}
      onMouseDown={keepClickOnTouch}
      onClick={() => onPick(name)}
    >
      <span className="catalog-browser__row-name">{name}</span>
      {count > 0 && (
        <span className="catalog-browser__row-count">{count} {count === 1 ? 'skelbimas' : 'skelbimai'}</span>
      )}
      <span className="catalog-browser__chip-arrow">›</span>
    </button>
  )
}
